/**
 * The voice dock.
 *
 * One small panel for the whole app. It appears when you join a channel,
 * follows you to every screen, and can be dragged anywhere - the position
 * sticks between sessions and is nudged back inside if the window shrinks.
 *
 * Everything about the call lives here and nowhere else: who is talking, the
 * round trip to the server, and the four controls.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Party } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import { Avatar } from './Avatar';
import { RoleTag, UserName, openUserCard } from './user';
import { Icon } from '@/icons/Icon';
import { DockButton, Hangup, Headset, HeadsetOff, Mic, MicOff, ScreenIcon, Sliders } from './dockUi';
import { useToast } from './ui';

const POS_KEY = 'pogu.voiceDock';
const MARGIN = 12;

export function VoiceDock({ poppedOut }: {
  /** True when the dock lives in its own window instead - the desktop app. */
  poppedOut: boolean;
}) {
  const voice = useVoice();
  const ping = useSession((s) => s.ping);
  const me = useSession((s) => s.me);
  const settings = useSession((s) => s.settings);
  const queryClient = useQueryClient();
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const clamp = useCallback((x: number, y: number) => {
    const box = ref.current?.getBoundingClientRect();
    const w = box?.width ?? 232;
    const h = box?.height ?? 120;
    return {
      x: Math.max(MARGIN, Math.min(x, Math.max(MARGIN, window.innerWidth - w - MARGIN))),
      y: Math.max(MARGIN, Math.min(y, Math.max(MARGIN, window.innerHeight - h - MARGIN)))
    };
  }, []);

  useLayoutEffect(() => {
    if (!voice.partyId || pos) return;
    let saved: { x: number; y: number } | null = null;
    try { saved = JSON.parse(localStorage.getItem(POS_KEY) ?? 'null'); } catch { /* never stored */ }
    const box = ref.current?.getBoundingClientRect();
    setPos(clamp(
      saved?.x ?? window.innerWidth - (box?.width ?? 232) - 18,
      saved?.y ?? window.innerHeight - (box?.height ?? 140) - 96
    ));
  }, [voice.partyId, pos, clamp]);

  useEffect(() => {
    if (!voice.partyId) { setPos(null); return; }
    const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [voice.partyId, clamp]);

  /* pointer-events drag: mouse, pen and touch in one path */
  const start = useRef<{ px: number; py: number; x: number; y: number; moved: boolean } | null>(null);

  function onDown(e: React.PointerEvent) {
    if ((e.target as HTMLElement).closest('button')) return;
    const box = ref.current!.getBoundingClientRect();
    start.current = { px: e.clientX, py: e.clientY, x: box.left, y: box.top, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
  }
  function onMove(e: React.PointerEvent) {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.px;
    const dy = e.clientY - s.py;
    if (!s.moved && Math.abs(dx) + Math.abs(dy) < 3) return;   // a click, not a drag
    s.moved = true;
    setPos(clamp(s.x + dx, s.y + dy));
  }
  function onUp(e: React.PointerEvent) {
    const s = start.current;
    start.current = null;
    setDragging(false);
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* gone */ }
    if (s?.moved && pos) {
      try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* storage off */ }
    }
  }

  // One dock at a time: while it is out in its own window, there is nothing
  // to draw in here.
  if (!voice.partyId || poppedOut) return null;

  const parties = queryClient.getQueriesData<{ parties: Party[] }>({ queryKey: ['parties'] });
  const party = parties.flatMap(([, d]) => d?.parties ?? []).find((p) => p.id === voice.partyId);

  const quality = settings?.screenQuality ?? 'balanced';
  const bars = ping === null ? 0 : ping < 80 ? 3 : ping < 180 ? 2 : 1;
  const barTone = ping === null ? 'bg-ghost' : ping < 80 ? 'bg-good' : ping < 180 ? 'bg-warn' : 'bg-bad';

  const myRole = party?.members.find((m) => m.userId === me?.id)?.role;
  const roleLabel = !party ? 'In voice'
    : party.hostId === me?.id ? 'Owner'
      : myRole === 'officer' ? 'Officer'
        : 'Member';

  return (
    <div
      ref={ref}
      style={pos ? { left: pos.x, top: pos.y } : { right: 18, bottom: 96 }}
      className={cn(
        'dock-in fixed z-[100] flex select-none items-center rounded-[calc(18px*var(--radius-mul,1))] p-2',
        'border border-line-soft bg-[color-mix(in_srgb,var(--bg-1)_96%,var(--voice))]',
        'shadow-[0_16px_40px_rgba(0,0,0,.42)]',
        'transition-[box-shadow,transform] duration-200 ease-[var(--ease-out-soft)]',
        dragging && 'shadow-[0_26px_60px_rgba(0,0,0,.55)]'
      )}
    >
      {/* the grab handle */}
      <span
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        title="Drag to move"
        className={cn(
          'grid touch-none grid-cols-2 gap-[3px] px-2 py-2 opacity-40 transition-opacity hover:opacity-90',
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        )}
      >
        {Array.from({ length: 6 }, (_, i) => (
          <i key={i} className="h-[3px] w-[3px] rounded-full bg-mute" />
        ))}
      </span>

      {collapsed ? (
        // Collapsed: just enough to prove you are still connected.
        <button
          type="button"
          title="Expand"
          onClick={() => setCollapsed(false)}
          className="flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-2 py-1.5 transition-colors hover:bg-surface-2"
        >
          <span className="flex items-end gap-[2px]">
            {[5, 8, 11].map((h, i) => (
              <i
                key={h}
                className={cn('w-[2.5px] rounded-full transition-colors', i < bars ? barTone : 'bg-surface-4')}
                style={{ height: h }}
              />
            ))}
          </span>
          <Icon name="chevronL" size={16} className="rotate-180 text-mute" />
        </button>
      ) : (
        <>
          {/* who you are and where */}
          <div className="flex min-w-0 items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 py-1.5 pr-3 pl-2">
            {me ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openUserCard(me, e.currentTarget); }}
                className="flex-none"
              >
                <Avatar user={me} size={34} presence />
              </button>
            ) : null}

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                {me ? <UserName user={me} className="truncate text-[13px] font-bold" /> : null}
                <RoleTag role={me?.role ?? 'user'} />
              </div>
              <div className="flex items-center gap-2 text-[11.5px]">
                <span className="text-mute">{roleLabel}</span>
                <span className="h-3 w-px bg-line" />
                <span className="flex items-center gap-1.5 text-voice">
                  <span className="flex items-end gap-[2px]">
                    {[5, 8, 11].map((h, i) => (
                      <i
                        key={h}
                        className={cn('w-[2.5px] rounded-full transition-colors', i < bars ? barTone : 'bg-surface-4')}
                        style={{ height: h }}
                      />
                    ))}
                  </span>
                  in voice
                </span>
              </div>
            </div>
          </div>

          {/* the call controls */}
          <div className="flex items-center gap-0.5 px-2">
            <DockButton
              label={voice.listenOnly ? 'No microphone connected' : voice.muted ? 'Unmute' : 'Mute'}
              on={voice.muted}
              onClick={() => voice.toggleMute()}
            >
              {voice.muted ? <MicOff /> : <Mic />}
            </DockButton>
            <DockButton
              label={voice.deafened ? 'Undeafen' : 'Deafen'}
              on={voice.deafened}
              onClick={() => voice.toggleDeafen()}
            >
              {voice.deafened ? <HeadsetOff /> : <Headset />}
            </DockButton>
            <DockButton
              // Already sharing? This swaps what you are showing. Stopping
              // lives on the right-click menu and in the picker itself.
              label={voice.sharing ? 'Stop sharing' : 'Share your screen'}
              on={voice.sharing}
              onClick={async () => {
                /* A toggle. It used to "switch source", which stopped the
                   share and immediately opened the picker again - so closing
                   a share brought the share window straight back. */
                try { if (voice.sharing) voice.stopScreenShare(); else await voice.startScreenShare(quality); }
                catch (err) { toast('Screen share failed: ' + (err as Error).message, 'bad'); }
              }}
            >
              <ScreenIcon />
            </DockButton>
            <DockButton label="Disconnect" danger onClick={() => void voice.leave()}>
              <Hangup />
            </DockButton>
          </div>

          {/* settings, and the way out of the expanded view */}
          <div className="flex items-center gap-0.5 border-l border-line pl-2">
            <DockButton label="Voice settings" onClick={() => go('settings')}>
              <Sliders />
            </DockButton>
            <button
              type="button"
              title="Collapse"
              onClick={() => setCollapsed(true)}
              className="grid h-8 w-7 place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-3 hover:text-ink"
            >
              <Icon name="chevronL" size={17} />
            </button>
          </div>
        </>
      )}

    </div>
  );
}


